import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../../../redux/store";
import {
  SettingItem,
  SettingPayload,
  SettingState,
} from "./types/setting-types";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;

  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }

  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }

  return payload;
}

export const fetchSettings = createAsyncThunk<
  SettingItem[],
  { page?: number; perPage?: number; search?: string } | undefined,
  { rejectValue: string; state: RootState }
>(
  "settings/fetch",
  async (params = { page: 1, perPage: 100 }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = (state as any).auth?.token;
    if (!token) return rejectWithValue("Unauthorized");

    try {
      const url = new URL(`${API_BASE}/settings`);
      url.searchParams.set("page", String(params.page ?? 1));
      url.searchParams.set("per_page", String(params.perPage ?? 100));
      if (params.search) url.searchParams.set("search", params.search);

      const res = await fetch(url.toString(), {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const payload: any = await handleResp(res);

      if (Array.isArray(payload)) return payload;
      if (payload?.data?.data && Array.isArray(payload.data.data))
        return payload.data.data;
      if (payload?.data && Array.isArray(payload.data)) return payload.data;
      return [];
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to fetch settings");
    }
  }
);

export const createSetting = createAsyncThunk<
  SettingItem,
  SettingPayload,
  { rejectValue: string; state: RootState }
>("settings/create", async (payload, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/settings`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data: any = await handleResp(res);
    return data?.data ?? data;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to create setting");
  }
});

export const updateSetting = createAsyncThunk<
  SettingItem,
  { id: number } & Partial<SettingPayload>,
  { rejectValue: string; state: RootState }
>("settings/update", async ({ id, ...payload }, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/settings/${id}`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data: any = await handleResp(res);
    return data?.data ?? data;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to update setting");
  }
});

export const deleteSetting = createAsyncThunk<
  number,
  number,
  { rejectValue: string; state: RootState }
>("settings/delete", async (id, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/settings/${id}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    await handleResp(res);
    return id;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to delete setting");
  }
});

export const refreshAppData = createAsyncThunk<
  string,
  void,
  { rejectValue: string; state: RootState }
>("settings/refreshAppData", async (_, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/app-data/generate`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const data: any = await handleResp(res);
    return data?.message ?? "Application data generated successfully";
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to refresh app data");
  }
});

const initialState: SettingState = {
  items: [],
  loading: false,
  saving: false,
  appDataRefreshing: false,
  error: null,
};

const settingSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    clearSettingError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch settings";
      })
      .addCase(createSetting.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createSetting.fulfilled, (state, action) => {
        state.saving = false;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createSetting.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to create setting";
      })
      .addCase(updateSetting.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSetting.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.items.findIndex((item) => item.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
      })
      .addCase(updateSetting.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to update setting";
      })
      .addCase(deleteSetting.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteSetting.fulfilled, (state, action) => {
        state.saving = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteSetting.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to delete setting";
      })
      .addCase(refreshAppData.pending, (state) => {
        state.appDataRefreshing = true;
        state.error = null;
      })
      .addCase(refreshAppData.fulfilled, (state) => {
        state.appDataRefreshing = false;
      })
      .addCase(refreshAppData.rejected, (state, action) => {
        state.appDataRefreshing = false;
        state.error = (action.payload as string) || "Failed to refresh app data";
      });
  },
});

export const { clearSettingError } = settingSlice.actions;

export const selectSettings = (state: RootState) => state.settings?.items ?? [];
export const selectSettingsLoading = (state: RootState) =>
  state.settings?.loading ?? false;
export const selectSettingsSaving = (state: RootState) =>
  state.settings?.saving ?? false;
export const selectSettingsError = (state: RootState) =>
  state.settings?.error ?? null;

export default settingSlice.reducer;
